import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar } from '@/components/ui/calendar';
import { 
  Cigarette, 
  Pill, 
  Coffee, 
  Smartphone,
  ShoppingCart,
  Gamepad2,
  Timer,
  TrendingUp,
  Target,
  Award,
  Clock,
  CheckCircle,
  AlertTriangle,
  CalendarDays,
  DollarSign,
  Wine
} from 'lucide-react';
import ReductionCounter from './gamification/ReductionCounter';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';

interface HabitData {
  type: 'smoking' | 'drugs' | 'caffeine' | 'screen_time' | 'shopping' | 'gaming' | 'alcohol' | 'custom';
  name?: string;
  amount?: number;
  unit?: string;
  frequency?: number;
  urge_strength?: number;
  urge_resisted?: boolean;
  triggers?: string[];
  coping_strategies?: string[];
  support_mode: boolean;
  streak_days?: number;
  reduction_goal?: number;
  delay_used?: number;
  notes?: string;
}

interface HabitTrackerProps {
  habitType: 'smoking' | 'drugs' | 'caffeine' | 'screen_time' | 'shopping' | 'gaming' | 'alcohol' | 'custom';
  initialData?: HabitData;
  supportMode?: boolean;
  onDataChange: (data: HabitData) => void;
}

const HABIT_CONFIGS = {
  smoking: {
    name: 'Smoking',
    icon: Cigarette,
    unit: 'cigarettes',
    color: 'text-destructive',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-destructive/30',
    greenThreshold: 0, // 0 cigarettes = green
    amberThreshold: 3, // 1-3 cigarettes = amber
    redThreshold: 4,   // 4+ cigarettes = red
    tips: [
      "Keep your hands busy with stress balls or fidget toys",
      "Change your routine - avoid smoking triggers",
      "Use the 4-7-8 breathing technique when cravings hit",
      "Reward yourself for each smoke-free day",
      "Tell supportive friends about your quit goals"
    ]
  },
  drugs: {
    name: 'Substance Use',
    icon: Pill,
    unit: 'uses',
    color: 'text-destructive',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-destructive/30',
    greenThreshold: 0, // 0 uses = green
    amberThreshold: 1, // 1 use = amber  
    redThreshold: 2,   // 2+ uses = red
    tips: [
      "Build a strong support network you can call",
      "Identify and avoid environmental triggers",
      "Practice grounding techniques: 5-4-3-2-1 method",
      "Create a safety plan for high-risk situations",
      "Celebrate small wins - every day counts"
    ]
  },
  caffeine: {
    name: 'Caffeine',
    icon: Coffee,
    unit: 'mg',
    color: 'text-primary',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-primary/30',
    greenThreshold: 200,  // 0-200mg = green
    amberThreshold: 400,  // 201-400mg = amber
    redThreshold: 401,    // 400+ mg = red
    tips: [
      "Gradually reduce intake by 25mg every few days",
      "Switch to green tea for lower caffeine + L-theanine",
      "Stay hydrated - dehydration worsens withdrawal",
      "Exercise to boost natural energy levels",
      "Get sunlight exposure to regulate energy naturally"
    ]
  },
  screen_time: {
    name: 'Screen Time',
    icon: Smartphone,
    unit: 'hours',
    color: 'text-secondary',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-secondary/30',
    greenThreshold: 2,  // 0-2 hours = green
    amberThreshold: 4,  // 2.1-4 hours = amber
    redThreshold: 5,    // 4+ hours = red
    tips: [
      "Use app timers and digital wellbeing controls",
      "Create phone-free zones (bedroom, meals)",
      "Replace scrolling with physical activities",
      "Turn off non-essential notifications",
      "Use grayscale mode to reduce visual appeal"
    ]
  },
  shopping: {
    name: 'Impulse Shopping',
    icon: ShoppingCart,
    unit: 'purchases',
    color: 'text-accent',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-accent/30',
    greenThreshold: 0,  // 0 purchases = green
    amberThreshold: 2,  // 1-2 purchases = amber
    redThreshold: 3,    // 3+ purchases = red
    tips: [
      "Use the 24-hour rule before any non-essential purchase",
      "Unsubscribe from marketing emails and social media ads",
      "Create a budget and track all expenses",
      "Find free activities that bring joy instead",
      "Ask 'Do I need this or just want it?'"
    ]
  },
  gaming: {
    name: 'Gaming',
    icon: Gamepad2,
    unit: 'hours',
    color: 'text-secondary',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-secondary/30',
    greenThreshold: 2,  // 0-2 hours = green
    amberThreshold: 4,  // 2.1-4 hours = amber  
    redThreshold: 5,    // 4+ hours = red
    tips: [
      "Set specific gaming windows and use timers",
      "Remove gaming apps from easy-access locations",
      "Find real-world activities that provide similar rewards",
      "Join communities focused on other interests",
      "Use gaming time as a reward after completing tasks"
    ]
  },
  alcohol: {
    name: 'Alcohol',
    icon: Wine,
    unit: 'units',
    color: 'text-secondary',
    bgColor: 'bg-gradient-therapy',
    borderColor: 'border-secondary/30',
    greenThreshold: 0,  // 0 units = green
    amberThreshold: 4,  // 1-4 units = amber
    redThreshold: 5,    // 5+ units = red
    tips: [
      "Track every drink honestly - awareness is the first step",
      "Identify your triggers: stress, social pressure, boredom",
      "Try alcohol-free alternatives (0% beers, mocktails)",
      "Set alcohol-free days each week (aim for 3+)",
      "Notice how you feel the next day - sleep, mood, energy",
      "Delay tactics: wait 20 minutes before each drink",
      "Have a maximum limit before you start drinking",
      "Tell a trusted friend about your reduction goals"
    ]
  }
};

const COMMON_TRIGGERS = [
  'Stress', 'Boredom', 'Social situations', 'Work pressure', 'Loneliness', 
  'Anxiety', 'Celebration', 'Habit/routine', 'Peer pressure', 'Depression'
];

const COPING_STRATEGIES = [
  'Deep breathing', 'Physical exercise', 'Call someone supportive', 'Meditation/mindfulness',
  'Drink water', 'Take a walk', 'Listen to music', 'Journaling', 'Creative activity',
  'Take a shower', 'Practice gratitude', 'Progressive muscle relaxation'
];

export default function HabitTracker({ habitType, initialData, supportMode = false, onDataChange }: HabitTrackerProps) {
  const [data, setData] = useState<HabitData>(
    initialData || {
      type: habitType,
      support_mode: supportMode,
      amount: 0,
      frequency: 0,
      streak_days: 0,
      triggers: [],
      coping_strategies: []
    }
  );

  const [delayTimer, setDelayTimer] = useState<number | null>(null);
  const [selectedTriggers, setSelectedTriggers] = useState<string[]>(data.triggers || []);
  const [selectedCoping, setSelectedCoping] = useState<string[]>(data.coping_strategies || []);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [historicalData, setHistoricalData] = useState<Record<string, 'green' | 'amber' | 'red'>>({});
  const [previousDayAmount, setPreviousDayAmount] = useState(0);
  const [costPerUnit, setCostPerUnit] = useState(getDefaultCost(habitType));
  const [showReduction, setShowReduction] = useState(false);
  const { toast } = useToast();

  function getDefaultCost(type: string): number {
    switch (type) {
      case 'smoking': return 0.50; // Per cigarette
      case 'caffeine': return 0.05; // Per mg
      case 'screen_time': return 0; // No direct cost
      case 'shopping': return 25; // Per purchase
      case 'gaming': return 0; // No direct cost
      default: return 0;
    }
  }

  const config = HABIT_CONFIGS[habitType];

  // Load historical data when component mounts
  useEffect(() => {
    loadHistoricalData();
  }, [calendarMonth]);

  const loadHistoricalData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const startDate = format(startOfMonth(calendarMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(calendarMonth), 'yyyy-MM-dd');

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('date, cravings_level, processed_food_level, exercise_duration')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (error) throw error;

      const historical: Record<string, 'green' | 'amber' | 'red'> = {};
      
      (data || []).forEach(entry => {
        let amount = 0;
        
        // Map different habit types to appropriate data fields
        if (habitType === 'smoking') {
          amount = entry.cravings_level || 0;
        } else if (habitType === 'caffeine') {
          amount = entry.cravings_level ? entry.cravings_level * 100 : 0;
        } else if (habitType === 'screen_time' || habitType === 'gaming') {
          amount = entry.exercise_duration ? Math.max(0, 8 - entry.exercise_duration) : 4;
        } else if (habitType === 'shopping') {
          amount = entry.processed_food_level || 0;
        }

        // Determine risk level
        if (amount <= config.greenThreshold) {
          historical[entry.date] = 'green';
        } else if (amount <= config.amberThreshold) {
          historical[entry.date] = 'amber';
        } else {
          historical[entry.date] = 'red';
        }
      });

      setHistoricalData(historical);
    } catch (error) {
      console.error('Error loading historical data:', error);
      toast({
        title: "Could not load history",
        description: "Using current session data only",
        variant: "default"
      });
    }
  };

  const handleDataChange = (field: keyof HabitData, value: any) => {
    const updatedData = { ...data, [field]: value };
    setData(updatedData);
    onDataChange(updatedData);
  };

  const startDelayTimer = (minutes: number) => {
    setDelayTimer(minutes);
    toast({
      title: `${minutes} minute delay started`,
      description: "Use this time to try a coping strategy. You're rewiring your brain!"
    });

    setTimeout(() => {
      setDelayTimer(null);
      toast({
        title: "Delay complete",
        description: "How are you feeling? Every pause builds your strength to choose differently."
      });
    }, minutes * 60 * 1000);
  };

  const logUrgeResisted = () => {
    handleDataChange('urge_resisted', true);
    const newStreak = (data.streak_days || 0) + 1;
    handleDataChange('streak_days', newStreak);
    
    toast({
      title: "Urge resisted! 🌟",
      description: `Amazing! You proved that urges don't control you. ${newStreak} day streak!`
    });
  };

  const getRiskLevel = () => {
    const amount = data.amount || 0;
    
    if (habitType === 'smoking') {
      if (amount <= config.greenThreshold) return 'green';
      if (amount <= config.amberThreshold) return 'amber';
      return 'red';
    } else if (habitType === 'caffeine') {
      if (amount <= config.greenThreshold) return 'green';
      if (amount <= config.amberThreshold) return 'amber';
      return 'red';
    } else if (habitType === 'screen_time' || habitType === 'gaming') {
      if (amount <= config.greenThreshold) return 'green';
      if (amount <= config.amberThreshold) return 'amber';
      return 'red';
    } else {
      // For shopping, drugs, etc.
      if (amount <= config.greenThreshold) return 'green';
      if (amount <= config.amberThreshold) return 'amber';
      return 'red';
    }
  };

  const getDynamicBgColor = () => {
    const level = getRiskLevel();
    switch (level) {
      case 'green': return 'bg-accent';
      case 'amber': return 'bg-primary';
      case 'red': return 'bg-destructive';
      default: return 'bg-muted';
    }
  };

  const getDynamicIconColor = () => {
    const level = getRiskLevel();
    switch (level) {
      case 'green': return 'text-accent';
      case 'amber': return 'text-primary';
      case 'red': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskMessage = () => {
    const level = getRiskLevel();
    const amount = data.amount || 0;
    
    if (amount === 0) {
      return "🌟 Great job! You're building new, healthier patterns.";
    }
    
    switch (level) {
      case 'green':
        return "✅ Within manageable limits. Keep working toward your goals.";
      case 'amber':
        return "⚠️ Consider using some reduction strategies today.";
      case 'red':
        return "🚨 High usage detected. Please prioritise your wellbeing.";
      default:
        return "";
    }
  };

  const getStreakMessage = () => {
    const streak = data.streak_days || 0;
    if (streak === 0) return "Ready to start your streak?";
    if (streak === 1) return "🎉 1 day strong! The hardest part is starting.";
    if (streak < 7) return `💪 ${streak} days! You're building momentum.`;
    if (streak < 30) return `🔥 ${streak} days! New habits are forming.`;
    return `🏆 ${streak} days! You're living proof that change is possible.`;
  };

  const IconComponent = config.icon;

  return (
    <div className="space-y-6">
      {/* Reduction Counter */}
      {showReduction && costPerUnit > 0 && (
        <ReductionCounter
          habitName={config.name}
          habitIcon={<IconComponent className="w-8 h-8" />}
          currentAmount={data.amount || 0}
          previousAmount={previousDayAmount}
          unit={config.unit}
          costPerUnit={costPerUnit}
          currency="USD"
          onCostUpdate={setCostPerUnit}
        />
      )}

      {/* Quick Actions */}
      {costPerUnit > 0 && (
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowReduction(!showReduction)}
            className="flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            {showReduction ? 'Hide' : 'Show'} Savings
          </Button>
        </div>
      )}
      {/* Current Status */}
      <Card className={`p-6 ${config.bgColor} ${config.borderColor} border-2`}>
        <div className="text-center mb-6">
          <div className={`w-16 h-16 ${getDynamicBgColor()} rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-primary-foreground/20`}>
            <IconComponent className={`w-8 h-8 ${getDynamicIconColor() === 'text-accent' ? 'text-accent-foreground' : 'text-primary-foreground'}`} />
          </div>
          <h3 className="text-2xl font-bold mb-2 text-foreground">
            {config.name} Today
          </h3>
          
          <div className="space-y-2">
            <div className="text-3xl font-bold text-foreground">
              {data.amount || 0} {config.unit}
            </div>
            <Badge 
              className={`text-sm px-4 py-1 ${
                getRiskLevel() === 'green' ? 'bg-accent text-accent-foreground' :
                getRiskLevel() === 'amber' ? 'bg-primary text-primary-foreground' :
                'bg-destructive text-destructive-foreground'
              }`}
            >
              {getRiskLevel().toUpperCase()} DAY - {data.amount || 0} {config.unit}
            </Badge>
            <p className="text-sm text-muted-foreground">
              {getRiskMessage()}
            </p>
          </div>
        </div>

        {/* Streak Counter */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Award className="w-5 h-5 text-primary" />
            <span className="font-semibold">Streak: {data.streak_days || 0} days</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {getStreakMessage()}
          </p>
        </div>

        {/* Usage Input */}
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Amount consumed today</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                type="number"
                min="0"
                value={data.amount || ''}
                onChange={(e) => handleDataChange('amount', Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-muted-foreground">{config.unit}</span>
            </div>
          </div>

          {data.reduction_goal && (
            <div>
              <div className="flex justify-between text-sm text-muted-foreground mb-1">
                <span>Daily goal progress</span>
                <span>{data.amount || 0} / {data.reduction_goal} {config.unit}</span>
              </div>
              <Progress 
                value={Math.min(((data.reduction_goal - (data.amount || 0)) / data.reduction_goal) * 100, 100)} 
                className="h-2"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Progress Calendar */}
      <Card className="p-6 bg-gradient-therapy border border-soft-accent/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-primary flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Monthly Progress Calendar
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            {showCalendar ? 'Hide' : 'Show'} Calendar
          </Button>
        </div>
        
        {showCalendar && (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-accent"></div>
                <span className="text-foreground">Green Days (Low Usage)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary"></div>
                <span className="text-foreground">Amber Days (Moderate)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-destructive"></div>
                <span className="text-foreground">Red Days (High Usage)</span>
              </div>
            </div>
            
            <div className="calendar-container bg-background rounded-lg p-4">
              <Calendar
                mode="single"
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                className="w-full mx-auto pointer-events-auto"
                components={{
                  DayContent: ({ date }) => {
                    const isToday = isSameDay(date, new Date());
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const riskLevel = historicalData[dateStr];
                    
                    return (
                      <div className={`
                        w-8 h-8 flex items-center justify-center text-sm font-medium rounded-md
                        transition-all duration-200 cursor-pointer
                        ${isToday ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}
                        ${riskLevel === 'green' ? 'bg-accent text-accent-foreground shadow-sm' : ''}
                        ${riskLevel === 'amber' ? 'bg-primary text-white shadow-sm' : ''}
                        ${riskLevel === 'red' ? 'bg-destructive text-white shadow-sm' : ''}
                        ${!riskLevel ? 'hover:bg-muted text-muted-foreground' : ''}
                      `}>
                        {format(date, 'd')}
                      </div>
                    );
                  }
                }}
              />
            </div>
            
            <div className="text-center text-xs text-muted-foreground mt-4">
              🎯 Aim for more green days each month. Every healthy choice counts!
            </div>
          </div>
        )}
      </Card>

      {/* Support Mode Features */}
      {supportMode && (
        <Card className={`p-6 ${config.bgColor} ${config.borderColor} border`}>
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Support Tools
          </h4>
          
          {/* Urge Tracking */}
          <div className="space-y-4 mb-6">
            <div>
              <Label className="text-sm font-medium">Urge strength today (0-10)</Label>
              <Slider
                value={[data.urge_strength || 0]}
                onValueChange={(value) => handleDataChange('urge_strength', value[0])}
                max={10}
                step={1}
                className="mt-2"
              />
              <div className="text-center text-sm text-muted-foreground mt-1">
                {data.urge_strength || 0}/10
              </div>
            </div>

            {/* Control Tools */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => startDelayTimer(5)}
                disabled={delayTimer !== null}
                className="flex items-center gap-1"
              >
                <Clock className="w-4 h-4" />
                5 min delay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startDelayTimer(15)}
                disabled={delayTimer !== null}
                className="flex items-center gap-1"
              >
                <Clock className="w-4 h-4" />
                15 min delay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={logUrgeResisted}
                className="flex items-center gap-1 text-accent border-accent/30"
              >
                <CheckCircle className="w-4 h-4" />
                Resisted urge
              </Button>
            </div>

            {delayTimer && (
              <div className={`p-3 rounded-lg border-2 ${config.borderColor} ${config.bgColor}`}>
                <p className="text-sm font-medium mb-1">
                  ⏰ Delay timer active: {delayTimer} minutes
                </p>
                <p className="text-xs text-muted-foreground">
                  Try a coping strategy below. You're building new neural pathways!
                </p>
              </div>
            )}
          </div>

          {/* Triggers */}
          <div className="mb-6">
            <Label className="text-sm font-medium mb-2 block">What triggered urges today?</Label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {COMMON_TRIGGERS.map((trigger) => (
                <div key={trigger} className="flex items-center space-x-2">
                  <Checkbox
                    id={trigger}
                    checked={selectedTriggers.includes(trigger)}
                    onCheckedChange={(checked) => {
                      const newTriggers = checked 
                        ? [...selectedTriggers, trigger]
                        : selectedTriggers.filter(t => t !== trigger);
                      setSelectedTriggers(newTriggers);
                      handleDataChange('triggers', newTriggers);
                    }}
                  />
                  <Label htmlFor={trigger} className="text-xs cursor-pointer">
                    {trigger}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Coping Strategies */}
          <div className="mb-4">
            <Label className="text-sm font-medium mb-2 block">Coping strategies used:</Label>
            <div className="grid grid-cols-2 gap-2">
              {COPING_STRATEGIES.map((strategy) => (
                <div key={strategy} className="flex items-center space-x-2">
                  <Checkbox
                    id={strategy}
                    checked={selectedCoping.includes(strategy)}
                    onCheckedChange={(checked) => {
                      const newCoping = checked 
                        ? [...selectedCoping, strategy]
                        : selectedCoping.filter(s => s !== strategy);
                      setSelectedCoping(newCoping);
                      handleDataChange('coping_strategies', newCoping);
                    }}
                  />
                  <Label htmlFor={strategy} className="text-xs cursor-pointer">
                    {strategy}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Notes & reflections</Label>
            <Textarea
              placeholder="How are you feeling? What's helping? What's challenging?"
              value={data.notes || ''}
              onChange={(e) => handleDataChange('notes', e.target.value)}
              className="text-sm"
            />
          </div>
        </Card>
      )}

      {/* Reduction Tips */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-accent" />
          Daily Reduction Tip
        </h4>
        <div className={`p-4 rounded-lg border-2 ${config.borderColor} ${config.bgColor}`}>
          <p className="text-sm">
            💡 {config.tips[Math.floor(Math.random() * config.tips.length)]}
          </p>
        </div>
      </Card>

      {/* Goal Setting */}
      <Card className="p-6">
        <h4 className="font-semibold mb-4">Set Your Reduction Goal</h4>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Daily limit ({config.unit})</Label>
            <Input
              type="number"
              min="0"
              value={data.reduction_goal || ''}
              onChange={(e) => handleDataChange('reduction_goal', Number(e.target.value))}
              placeholder="Set a realistic daily limit"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Start with a small reduction from your current level. You can always adjust as you progress.
          </p>
        </div>
      </Card>
    </div>
  );
}